import { pickFirstCompanyRoleValueFromRecords } from './taskeo-company-role-response.util';

/**
 * Leitura best-effort do payload JWT (sem validar assinatura).
 * Taskeo: `companyRoleValue` (string numérica) + `companyRole` / ClaimTypes.Role (nome do enum).
 */

function asJwtRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : {};
}

function base64UrlToUtf8(segment: string): string {
  let s = segment.replace(/-/g, '+').replace(/_/g, '/');
  while (s.length % 4) s += '=';
  try {
    return decodeURIComponent(
      Array.prototype.map
        .call(atob(s), (c: string) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
  } catch {
    return '';
  }
}

export function decodeJwtPayloadJson(accessToken: string): Record<string, unknown> | null {
  const t = accessToken?.trim();
  if (!t) return null;
  const parts = t.split('.');
  if (parts.length < 2) return null;
  try {
    const json = base64UrlToUtf8(parts[1]);
    if (!json) return null;
    const o = JSON.parse(json) as unknown;
    return o && typeof o === 'object' && !Array.isArray(o) ? (o as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

/**
 * Extrai valor bruto do claim de papel da empresa (vários nomes usados por .NET / APIs).
 * Alguns tokens colocam `companyRole` / `roleValue` dentro de `company`, `user` ou `membership`.
 */
export function readCompanyRoleRawFromJwtPayload(payload: Record<string, unknown>): unknown {
  const company = asJwtRecord(payload['company'] ?? payload['Company']);
  const user = asJwtRecord(payload['user'] ?? payload['User']);
  const tenant = asJwtRecord(payload['tenant'] ?? payload['Tenant']);
  const membership = asJwtRecord(payload['membership'] ?? payload['Membership']);
  const nested = [payload, company, user, tenant, membership].filter((o) => o && Object.keys(o).length > 0);

  const fromValue = pickFirstCompanyRoleValueFromRecords(nested);
  if (fromValue !== undefined) return fromValue;

  const directKeys = [
    'role',
    'Role',
    'company_role',
    'companyRole',
    'CompanyRole',
    'user_role',
    'userRole',
    'UserRole',
    'membershipRole',
    'MembershipRole',
    'tenant_role',
    'TenantRole',
  ];
  for (const obj of nested) {
    for (const k of directKeys) {
      const v = obj[k];
      if (v !== undefined && v !== null) return v;
    }
  }
  for (const [k, v] of Object.entries(payload)) {
    if (v === undefined || v === null) continue;
    const kl = k.toLowerCase();
    if (
      kl === 'http://schemas.microsoft.com/ws/2008/06/identity/claims/role' ||
      kl.endsWith('/role') ||
      kl.endsWith('claims/role')
    ) {
      return v;
    }
  }
  return undefined;
}

export function readCompanyRoleRawFromAccessToken(accessToken: string): unknown {
  const payload = decodeJwtPayloadJson(accessToken);
  if (!payload) return undefined;
  return readCompanyRoleRawFromJwtPayload(payload);
}

/**
 * Claim de tenant/empresa no access token (quando a API inclui — usado para não aplicar
 * `companyRole` de outro contexto após troca de empresa).
 */
export function readScopedCompanyIdFromJwtPayload(
  payload: Record<string, unknown> | null
): string | undefined {
  if (!payload) return undefined;
  const keys = [
    'companyId',
    'CompanyId',
    'company_id',
    'Company_Id',
    'tenantId',
    'TenantId',
    'tid',
  ];
  for (const k of keys) {
    const v = payload[k];
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return undefined;
}

/** Compara GUID de empresa com ou sem hífenes. */
export function jwtScopedCompanyIdMatchesActive(
  jwtCompanyId: string | undefined,
  activeCompanyId: string | undefined
): boolean {
  const j = jwtCompanyId?.trim();
  const a = activeCompanyId?.trim();
  if (!j || !a) return false;
  const jn = j.replace(/-/g, '').toLowerCase();
  const an = a.replace(/-/g, '').toLowerCase();
  if (jn.length >= 32 && an.length >= 32) return jn === an;
  return j.toLowerCase() === a.toLowerCase();
}
